import axios from 'axios';
import {
    clearGeoSession,
    createFreshGeoSession,
    ensureGeoSession,
    getGeoSessionId,
    sendGeoSearchMessage,
    setGeoSessionId,
    setGeoSessionScope,
    type GeoChatSearchResponse,
} from '@/lib/geoSearchApi';
import { notifyBillingBalanceChanged } from '@/lib/billingEvents';
import { detectLocationForSearch, getStoredUserLocation } from '@/lib/location';
import { resolveGeoSearchScope, type GeoSearchScope } from '@/lib/geoScope';

export type AssistantGeoSearchResult = {
    response: GeoChatSearchResponse;
    scope: GeoSearchScope;
    sessionId: string;
};

export type AssistantGeoAnchor = {
    lat?: number;
    lng?: number;
    area_name?: string;
};

const MY_LOCATION_PATTERN =
    /\b(?:(?:my|user'?s?)\s+(?:current\s+)?location|current\s+location|near\s+me|around\s+me|where\s+i\s+am|from\s+here)\b/i;

export function messageRequestsMyLocation(message?: string): boolean {
    return Boolean(message?.trim() && MY_LOCATION_PATTERN.test(message));
}

export function resolveMyLocationAnchor(): AssistantGeoAnchor | undefined {
    const loc = getStoredUserLocation();
    if (loc?.lat == null || loc?.lon == null || loc.catalog_enabled === false || loc.source !== 'gps') {
        return undefined;
    }
    return {
        lat: loc.lat,
        lng: loc.lon,
        area_name: 'My location',
    };
}

export class MyLocationUnavailableError extends Error {
    readonly code = 'my_location_unavailable' as const;

    constructor(message: string) {
        super(message);
        this.name = 'MyLocationUnavailableError';
        Object.setPrototypeOf(this, MyLocationUnavailableError.prototype);
    }
}

export function isMyLocationUnavailableError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    return (
        error instanceof MyLocationUnavailableError ||
        (error as Record<string, unknown>).name === 'MyLocationUnavailableError' ||
        (error as Record<string, unknown>).code === 'my_location_unavailable' ||
        (error as Error).message === 'Location access was denied or unavailable.'
    );
}

/** Resolve GPS anchor for my-location queries, prompting the browser when needed (#119). */
export async function resolveSearchAnchor(
    message: string,
    anchor?: AssistantGeoAnchor,
): Promise<AssistantGeoAnchor | undefined> {
    if (anchor?.lat != null && anchor?.lng != null) {
        return anchor;
    }
    if (!messageRequestsMyLocation(message)) {
        return undefined;
    }
    const stored = resolveMyLocationAnchor();
    if (stored) {
        return stored;
    }
    const detected = await detectLocationForSearch();
    if ('error' in detected) {
        throw new MyLocationUnavailableError(detected.error);
    }
    return {
        lat: detected.lat,
        lng: detected.lng,
        area_name: 'My location',
    };
}

/** Offline Hyderabad neighborhood coords (mirrors backend hyderabad_areas). */
const KNOWN_AREA_COORDS: Record<string, { lat: number; lng: number }> = {
    'banjara hills': { lat: 17.4156, lng: 78.4347 },
    'hitec city': { lat: 17.4435, lng: 78.3772 },
    gachibowli: { lat: 17.4401, lng: 78.3489 },
    madhapur: { lat: 17.4487, lng: 78.39 },
    kondapur: { lat: 17.47, lng: 78.353 },
    secunderabad: { lat: 17.4344, lng: 78.5012 },
    miyapur: { lat: 17.4967, lng: 78.3576 },
};

function normalizeAreaKey(name: string): string {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function resolveKnownAreaCoords(
    areaName: string | undefined,
): { lat: number; lng: number } | undefined {
    if (!areaName?.trim()) return undefined;
    const key = normalizeAreaKey(areaName);
    if (KNOWN_AREA_COORDS[key]) return KNOWN_AREA_COORDS[key];
    for (const [known, coords] of Object.entries(KNOWN_AREA_COORDS)) {
        if (key.includes(known) || known.includes(key)) return coords;
    }
    return undefined;
}

/** Build client_context aligned with GeoSearchChatPage, preferring explicit anchors over prefs. */
export function buildAssistantGeoClientContext(
    scope: GeoSearchScope,
    locale: string,
    anchor?: AssistantGeoAnchor,
    message?: string,
): {
    country_code: string;
    city_id: number;
    city_name: string;
    locale: string;
    area_name?: string;
    anchor_lat?: number;
    anchor_lng?: number;
} {
    const explicitAnchor =
        anchor?.lat != null && anchor?.lng != null
            ? anchor
            : messageRequestsMyLocation(message)
              ? resolveMyLocationAnchor()
              : undefined;
    const hasExplicitAnchor = explicitAnchor?.lat != null && explicitAnchor?.lng != null;
    const explicitAreaName = explicitAnchor?.area_name?.trim();

    return {
        country_code: scope.countryCode,
        city_id: scope.cityId,
        city_name: scope.cityName,
        locale,
        ...(explicitAreaName ? { area_name: explicitAreaName } : {}),
        ...(hasExplicitAnchor
            ? { anchor_lat: explicitAnchor.lat, anchor_lng: explicitAnchor.lng }
            : {}),
    };
}

export async function runAssistantGeoSearch(
    message: string,
    locale: string,
    anchor?: AssistantGeoAnchor,
    existingSessionId?: string | null,
    organizationId?: string | null,
): Promise<AssistantGeoSearchResult | null> {
    const scope = await resolveGeoSearchScope();
    if (!scope) return null;

    let sessionId = existingSessionId?.trim() || '';
    if (!sessionId) {
        sessionId = await createFreshGeoSession();
    } else {
        setGeoSessionId(sessionId);
    }

    const scopeParams = {
        session_id: sessionId,
        country_code: scope.countryCode,
        city_id: scope.cityId,
        city_name: scope.cityName,
        city_lat: scope.cityLat,
        city_lng: scope.cityLng,
    };

    await setGeoSessionScope(scopeParams);

    const resolvedAnchor = await resolveSearchAnchor(message, anchor);

    const messageBody = {
        session_id: sessionId,
        message,
        organization_id: organizationId || undefined,
        client_context: buildAssistantGeoClientContext(scope, locale, resolvedAnchor, message),
    };

    let response: GeoChatSearchResponse;
    try {
        response = await sendGeoSearchMessage(messageBody);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
            clearGeoSession();
            sessionId = await ensureGeoSession();
            await setGeoSessionScope({ ...scopeParams, session_id: sessionId });
            response = await sendGeoSearchMessage({ ...messageBody, session_id: sessionId });
        } else {
            throw err;
        }
    }

    notifyBillingBalanceChanged();
    return { response, scope, sessionId };
}
